import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { JobOrderInfoService } from '../../services/job-order-info.service';
import { JobOrderInfoResponse, JobOrderApprover, JobOrderRep } from '../../models/job-order-info.model';
import { AuthService } from '../../../services/auth.service';
import { ChangeRequestService } from '../../services/change-request.service';
import { 
  ChangeRequestHistory, 
  ChangeRequestType, 
  CreateChangeRequestDto 
} from '../../models/change-request.model';

@Component({
  selector: 'app-asc-jobinfo',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './asc-jobinfo.html',
  styleUrl: './asc-jobinfo.scss'
})
export class AscJobinfoComponent implements OnInit {
  jobOrderId: string = '';
  jobOrderData: JobOrderInfoResponse | null = null;
  errorMessage: string = '';
  isLoading: boolean = false;

  // 変更申請関連
  changeRequestHistories: ChangeRequestHistory[] = [];
  changeRequestTypes: ChangeRequestType[] = [];
  showRequestBlock: boolean = false;
  showRequestForm: boolean = false;
  isRequestButtonEnabled: boolean = false;
  showCancelButton: boolean = false;

  // 書き込み権限
  readWrite: boolean = false;
  adminLevel: number = 0;

  // 承認者メールアドレス（変更検知用に元の値を保持）
  originalEmails: Map<number, string> = new Map();

  // MP担当者
  reps: JobOrderRep[] = [];

  // 申請フォーム
  selectedChangeType: number | null = null;
  requestComment: string = '';
  tsDeleteStartDate: string = '';
  mngEmail: string = '';

  // ナビゲーション用イベントエミッター
  @Output() navigateToAsc = new EventEmitter<{ userCode?: string; ascUq?: string }>();
  @Output() navigateToCustomer = new EventEmitter<number>();
  @Output() navigateToTs = new EventEmitter<{ ascUq?: string; userCode?: string; yearMonth?: string; timesheetId?: number }>();

  constructor(
    private jobOrderInfoService: JobOrderInfoService,
    private authService: AuthService,
    private changeRequestService: ChangeRequestService
  ) {}

  ngOnInit(): void {
    // ReadWriteを取得
    this.readWrite = this.authService.getReadWrite();
    this.adminLevel = this.authService.getAdminLevel() || 0;
  }

  // 画面を初期状態に戻す
  onClear(): void {
    this.jobOrderId = '';
    this.jobOrderData = null;
    this.errorMessage = '';
    this.isLoading = false;
    this.changeRequestHistories = [];
    this.changeRequestTypes = [];
    this.showRequestBlock = false;
    this.showRequestForm = false;
    this.isRequestButtonEnabled = false;
    this.showCancelButton = false;
    this.selectedChangeType = null;
    this.requestComment = '';;
    this.tsDeleteStartDate = '';
    this.mngEmail = '';
    this.reps = [];
  }

  // 数字のみ入力を許可
  onNumberKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  // 獲注情報を読み込み
  loadJobOrderInfo(jobOrderId?: number): void {
    // 引数が渡された場合はそれを使用
    if (jobOrderId !== undefined) {
      this.jobOrderId = jobOrderId.toString();
    }

    // 入力チェック
    if (!this.jobOrderId || this.jobOrderId.trim() === '') {
      this.errorMessage = '獲注IDを入力してください';
      return;
    }

    const id = parseInt(this.jobOrderId, 10);
    if (isNaN(id) || id <= 0) {
      this.errorMessage = '正しい獲注IDを入力してください（9桁の数字）';
      return;
    }

    // データ取得
    this.isLoading = true;
    this.errorMessage = '';
    this.jobOrderData = null;

    this.jobOrderInfoService.getJobOrderInfo(id).subscribe({
      next: (data) => {
        // タイムシートデータを年月度の降順にソート
        if (data.timesheets && data.timesheets.length > 0) {
          data.timesheets.sort((a, b) => {
            // yearMonthを比較（例: "2025-12" > "2025-11"）
            if (a.yearMonth > b.yearMonth) return -1;
            if (a.yearMonth < b.yearMonth) return 1;
            return 0;
          });
        }
        
        this.jobOrderData = data;
        this.isLoading = false;
        this.originalEmails = new Map(
          (data.approvers ?? []).map(a => [a.userId, a.email2])
        );

        if (!data.basic) {
          this.errorMessage = '指定された獲注IDのデータが見つかりませんでした';
        } else {
          // 変更申請履歴を取得
          this.loadChangeRequestHistory(id);
          // MP担当者を取得
          this.jobOrderInfoService.getReps(id).subscribe({
            next: (reps) => { this.reps = reps; },
            error: () => { this.reps = []; }
          });
        }
      },
      error: (error) => {
        console.error('獲注情報の取得に失敗しました:', error);
        this.errorMessage = 'データの取得に失敗しました。もう一度お試しください。';
        this.isLoading = false;
      }
    });
  }

  // 台帳ダウンロード
  downloadLedger(): void {
    if (!this.jobOrderId) {
      alert('獲注IDが設定されていません');
      return;
    }

    const id = parseInt(this.jobOrderId, 10);
    this.jobOrderInfoService.downloadLedgerExcel(id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `JobOrder_${id}_Ledger.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('台帳ダウンロードエラー:', error);
        alert('台帳のダウンロードに失敗しました');
      }
    });
  }

  // PDF出力（顧客用）
  downloadPdf(): void {
    if (!this.jobOrderId) {
      alert('獲注IDが設定されていません');
      return;
    }

    const id = parseInt(this.jobOrderId, 10);
    this.jobOrderInfoService.downloadLedgerPdf(id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const yearMonthRaw = this.jobOrderData?.timesheets?.[0]?.yearMonth ?? '';
        const yearMonth = yearMonthRaw.length === 6
          ? `${yearMonthRaw.slice(0, 4)}-${yearMonthRaw.slice(4)}`
          : yearMonthRaw;
        const userCode = this.jobOrderData?.basic?.userCode ?? '';
        link.download = `ledger-${yearMonth}-${userCode}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('PDF出力エラー:', error);
        alert('PDFの出力に失敗しました');
      }
    });
  }

  // メールリセット
  resetEmail(userId: number): void {
    if (!confirm('メールアドレスをリセットしますか？')) {
      return;
    }

    this.jobOrderInfoService.resetEmail(userId).subscribe({
      next: () => {
        alert('メールアドレスをリセットしました');
        this.loadJobOrderInfo();
      },
      error: (error) => {
        console.error('メールリセットエラー:', error);
        alert('メールリセットに失敗しました');
      }
    });
  }

  // 承認者メールアドレス変更確認
  isEmailChanged(approver: JobOrderApprover): boolean {
    return approver.email2 !== this.originalEmails.get(approver.userId);
  }

  // 承認者メールアドレス保存
  saveApproverEmail(approver: JobOrderApprover): void {
    const email = approver.email2?.trim() ?? '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('有効なメールアドレスを入力してください。');
      return;
    }

    this.jobOrderInfoService.updateEmail(approver.userId, email).subscribe({
      next: () => {
        this.originalEmails.set(approver.userId, email);
        alert('メールアドレスを更新しました');
      },
      error: (error) => {
        console.error('メールアドレス更新エラー:', error);
        alert('メールアドレスの更新に失敗しました');
      }
    });
  }

  // パスワードリセット
  sendPasswordReminder(userId: number): void {
    const approver = this.jobOrderData?.approvers?.find(a => a.userId === userId);
    const name = approver?.userName ?? '';

    if (!confirm(`${name}さんのパスワードをリセットします。よろしいですか?`)) {
      return;
    }

    this.jobOrderInfoService.resetPassword(userId).subscribe({
      next: (result) => {
        if (result) {
          alert('パスワードをリセットしました。');
          this.loadJobOrderInfo();
        } else {
          alert('パスワードのリセットに失敗しました。');
        }
      },
      error: (error) => {
        console.error('パスワードリセットエラー:', error);
        alert('パスワードのリセット中にエラーが発生しました。');
      }
    });
  }

  // ナビゲーションメソッド
  onAscClick(userCode: string): void {
    this.navigateToAsc.emit({ userCode });
  }

  onAscUqClick(staffId: number): void {
    this.navigateToAsc.emit({ ascUq: staffId.toString() });
  }

  onCustomerOfficeClick(customerOfficeId: number): void {
    this.navigateToCustomer.emit(customerOfficeId);
  }

  onTimesheetClick(timesheet: any): void {
    if (this.jobOrderData?.basic) {
      // ASC UQ（staffId）と登録番号（userCode）を渡す
      this.navigateToTs.emit({
        ascUq: this.jobOrderData.basic.staffId?.toString(),
        userCode: this.jobOrderData.basic.userCode,
        yearMonth: timesheet.yearMonth,
        timesheetId: timesheet.timesheetId
      });
    }
  }

  // 変更申請履歴を取得
  loadChangeRequestHistory(jobOrderId: number): void {
    // フォームをリセット
    this.resetRequestForm();

    this.changeRequestService.getChangeRequestHistory(jobOrderId).subscribe({
      next: (response) => {
        this.changeRequestHistories = response.histories || [];
        
        if (this.changeRequestHistories.length === 0) {
          // 申請履歴がない場合
          this.showRequestBlock = false;
          this.isRequestButtonEnabled = true;
        } else {
          // 申請履歴がある場合
          this.showRequestBlock = true;
          const latestStatus = this.changeRequestHistories[0].procStatus;
          
          // ProcStatusが9（処理完了）の場合は申請ボタン有効、かつフォームを自動表示
          this.isRequestButtonEnabled = (latestStatus === 9);
          this.showRequestForm = (latestStatus === 9);

          if (latestStatus === 9) {
            this.ensureChangeRequestTypesLoaded();
          }
          
          // 未処理の申請がある場合のみ取消ボタンを表示（確定待ち(6)・処理完了(9)は非表示）
          this.showCancelButton = (latestStatus !== 6 && latestStatus !== 9);
        }
      },
      error: (error) => {
        console.error('変更申請履歴の取得に失敗しました:', error);
        // エラー時もボタンは有効にする
        this.showRequestBlock = false;
        this.isRequestButtonEnabled = true;
      }
    });
  }

  // 申請フォームをリセット
  resetRequestForm(): void {
    this.selectedChangeType = null;
    this.requestComment = '';
    this.tsDeleteStartDate = '';
    this.mngEmail = '';
    this.showRequestForm = false;
  }

  // 変更種類のドロップダウンを取得
  private ensureChangeRequestTypesLoaded(): void {
    if (this.changeRequestTypes.length > 0) {
      return;
    }

    this.changeRequestService.getChangeRequestTypes().subscribe({
      next: (types: any) => {
        this.changeRequestTypes = (types || []).filter((t: any) =>
          t.itemName === '獲注削除' || t.itemName === '期間短縮'
        );
        this.selectedChangeType = null;
      },
      error: (error: any) => {
        console.error('変更理由の取得に失敗しました:', error);
      }
    });
  }

  // 削除/変更申請ボタンクリック
  onChangeRequestClick(): void {
    if (!this.isRequestButtonEnabled) {
      return;
    }

    // フォームを完全にリセット
    this.resetRequestForm();
    
    // 申請ブロックを表示
    this.showRequestBlock = true;
    this.showCancelButton = false;

    this.ensureChangeRequestTypesLoaded();
    this.selectedChangeType = null;
    this.showRequestForm = true;
  }


  // TS削除開始日の入力制御（yyyy/MM/dd形式） - 獲注削除が選択された場合は注文日を初期値として設定し、編集不可にする
  isTsDeleteStartDateLocked(): boolean {
    const selectedType = this.changeRequestTypes.find(
      t => t.typeId === this.selectedChangeType
    );
    return selectedType?.itemName === '獲注削除';
  }

  // TS削除開始日の入力制御（yyyy/MM/dd形式）
  onChangeTypeChange(): void {
    const selectedType = this.changeRequestTypes.find(t => t.typeId === this.selectedChangeType);
    if (selectedType?.itemName === '獲注削除') {
      const orderDate = this.jobOrderData?.basic?.orderDate;
      if (orderDate) {
        // "YYYY/MM/DD～YYYY/MM/DD" の先頭部分（開始日）を取得
        this.tsDeleteStartDate = orderDate.split('～')[0].trim();
      }
    } else {
      this.tsDeleteStartDate = '';
    }
  }

  // TS削除開始日の入力制御（yyyy/MM/dd形式）
  formatDateInput(event: Event): void {
    if (this.isTsDeleteStartDateLocked()) {
      return;
    }
    if ((event as InputEvent).isComposing) return;
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // 数字以外を削除
    
    if (value.length >= 8) {
      value = value.substring(0, 8);
      this.tsDeleteStartDate = `${value.substring(0, 4)}/${value.substring(4, 6)}/${value.substring(6, 8)}`;
    } else if (value.length >= 6) {
      this.tsDeleteStartDate = `${value.substring(0, 4)}/${value.substring(4, 6)}/${value.substring(6)}`;
    } else if (value.length >= 4) {
      this.tsDeleteStartDate = `${value.substring(0, 4)}/${value.substring(4)}`;
    } else {
      this.tsDeleteStartDate = value;
    }
  }

  // 日付の妥当性チェック
  isValidDate(dateString: string): boolean {
    const regex = /^\d{4}\/\d{2}\/\d{2}$/;
    if (!regex.test(dateString)) {
      return false;
    }
    
    const parts = dateString.split('/');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && 
           date.getMonth() === month - 1 && 
           date.getDate() === day;
  }

  // CloseDate (0=末日, 15=15日) を「末日」「15日」に変換
  formatCloseDate(closeDate: number): string {
    return closeDate === 15 ? '15日' : '末日';
  }

  // メールアドレスの妥当性チェック
  isValidEmail(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  // 申請する
  submitChangeRequest(): void {
    // バリデーション
    if (this.selectedChangeType == null) {
      alert('削除/変更種類を選択してください');
      return;
    }

    if (!this.tsDeleteStartDate || !this.isValidDate(this.tsDeleteStartDate)) {
      alert('有効な日付を入力してください（yyyy/MM/dd形式）');
      return;
    }

    if (!this.mngEmail || !this.isValidEmail(this.mngEmail)) {
      alert('有効なメールアドレスを入力してください');
      return;
    }

    // 確認アラート
    if (!confirm('この獲注の削除/変更を申請します。よろしいですか？')) {
      return;
    }

    // 選択された変更理由テキストを取得
    const selectedType = this.changeRequestTypes.find(t => t.typeId === this.selectedChangeType);
    const reasonText = selectedType ? selectedType.itemName : '';

    // 申請データの作成
    const userId = parseInt(sessionStorage.getItem('UserID') || '0', 10);
    const request: CreateChangeRequestDto = {
      changeType: this.selectedChangeType,
      jobOrderId: parseInt(this.jobOrderId, 10),
      cutDate: this.tsDeleteStartDate,
      mngEmail: this.mngEmail,
      comment: this.requestComment || null,
      reasonText: reasonText
    };

    // API呼び出し
    this.changeRequestService.createChangeRequest(request).subscribe({
      next: (response: any) => {
        // レスポンスの成功判定を追加
        if (response && response.success && response.requestId) {
          alert('申請が完了しました。メールが送信されました。');
          
          // フォームをリセット
          this.resetRequestForm();
          this.showRequestForm = false;
          this.isRequestButtonEnabled = false;
          
          // 申請ブロックを再描画
          this.loadChangeRequestHistory(parseInt(this.jobOrderId, 10));
        } else {
          // APIは成功したが、申請は失敗
          const errorMsg = response?.message || '申請の処理に失敗しました。';
          alert(errorMsg);
          
          // エラー時は状態を元に戻す
          this.isRequestButtonEnabled = true;
        }
      },
      error: (error: any) => {
        const errorMsg = error?.error?.message || error?.message || '申請に失敗しました。';
        alert(errorMsg);
        
        // エラー時は状態を元に戻す
        this.isRequestButtonEnabled = true;
      }
    });
  }

  // 申請を取消す
  cancelChangeRequest(): void {
    if (!confirm('申請を取り消します。よろしいですか？')) {
      return;
    }

    this.changeRequestService.cancelChangeRequest(parseInt(this.jobOrderId, 10)).subscribe({
      next: (response: any) => {
        alert('申請を取り消しました。');
        
        // 状態を完全にリセット
        this.resetRequestForm();
        this.showRequestForm = false;
        this.showRequestBlock = false;
        this.showCancelButton = false;
        this.isRequestButtonEnabled = true;
        
        // 申請履歴を再取得
        this.loadChangeRequestHistory(parseInt(this.jobOrderId, 10));
      },
      error: (error: any) => {
        console.error('申請の取消に失敗しました:', error);
        const errorMsg = error?.error?.message || error?.message || '申請の取消に失敗しました。もう一度お試しください。';
        alert(errorMsg);
      }
    });
  }
}
